import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CimSubstationService } from '../../../../services/cim-substation.service';
import { SubstationTemplateService } from '../../../../services/substation-template.service';
import { SubstationTemplateForSelect } from '../../../../models/network-structure.model';
import { SearchSubstationDto, SelectSubstationDto } from '../../../../models/substation-template-instance.model';
import { ObjectDropdownComponent } from '../../../common/object-dropdown/object-dropdown.component';

export interface SelectedSubstationForSchematic {
  id: number;
  substationId: number;
  name: string;
  substationTemplateId: number;
  substationTemplateName: string;
}

@Component({
  selector: 'lib-nec-add-new-instance-equipment',
  templateUrl: './nec-add-new-instance-equipment.component.html',
  styleUrls: ['./nec-add-new-instance-equipment.component.scss', '../../network-entity-classes.component.scss'],
  standalone: false,
})
export class NecAddNewInstanceEquipmentComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Output() closed = new EventEmitter<void>();
  @Output() proceed = new EventEmitter<SelectedSubstationForSchematic>();

  @ViewChild('substationTemplateDropdown') substationTemplateDropdown!: ObjectDropdownComponent;
  @ViewChild('substationInstanceDropdown') substationInstanceDropdown!: ObjectDropdownComponent;

  substationTemplateOptions: SubstationTemplateForSelect[] = [];
  substationOptions: SelectSubstationDto[] = [];

  substationTemplateValue: SubstationTemplateForSelect | null = null;
  substationInstanceValue: SelectSubstationDto | null = null;
  selectedSubstationTemplateId: number = 0;

  dataIn: SearchSubstationDto = new SearchSubstationDto();

  private skipNextInstanceFetch = false;

  constructor(
    private cimSubstationService: CimSubstationService,
    private substationTemplateService: SubstationTemplateService,
    private toastr: ToastrService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
      this.getAllSubstationTemplatesForSelect();
    }
  }

  getAllSubstationTemplatesForSelect() {
    this.substationTemplateService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.substationTemplateOptions = responseData.data.map((substationTemplate: SubstationTemplateForSelect) => ({
            ...substationTemplate
          }));
        } else if (response?.status === 204) {
          this.substationTemplateOptions = [];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while loading substation templates');
      },
    });
  }

  onSelectSubstationTemplate(obj: SubstationTemplateForSelect | null) {
    this.substationTemplateValue = obj;
    this.substationInstanceValue = null;
    this.substationInstanceDropdown?.selectItem(null);
    this.substationOptions = [];

    if (obj?.id != null) {
      this.selectedSubstationTemplateId = obj.id;
      this.getAllSubstationInstancesForType(obj.id);
      this.skipNextInstanceFetch = true;
    } else {
      this.selectedSubstationTemplateId = 0;
      this.skipNextInstanceFetch = false;
    }
  }

  onSelectSubstationInstance(obj: SelectSubstationDto | null) {
    this.substationInstanceValue = obj;
  }

  onSearchTextChange(inputText: string) {
    if (this.selectedSubstationTemplateId > 0) {
      if (inputText === '' && this.skipNextInstanceFetch) {
        this.skipNextInstanceFetch = false;
        return;
      }
      this.getAllSubstationInstancesForType(this.selectedSubstationTemplateId, inputText);
    }
  }

  getAllSubstationInstancesForType(templateId: number, searchTerm: string = '') {
    this.dataIn.templateId = templateId;
    this.dataIn.searchTerm = searchTerm;
    this.cimSubstationService.getAllForSelect(this.dataIn).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        this.substationOptions = responseData?.status === 'OK' ? responseData.data : [];
      },
      error: () => {
        this.toastr.error('Error occurred while loading substation options');
      },
    });
  }

  onProceed() {
    if (!this.substationTemplateValue || this.substationTemplateValue.id == null) {
      this.toastr.warning('Please select a parent class (substation)');
      return;
    }
    if (!this.substationInstanceValue || this.substationInstanceValue.id == null) {
      this.toastr.warning('Please select an asset name');
      return;
    }

    this.proceed.emit({
      id: this.substationInstanceValue.substationTemplateInstanceId,
      substationId: this.substationInstanceValue.id,
      name: this.substationInstanceValue.name,
      substationTemplateId: this.substationTemplateValue.id,
      substationTemplateName: this.substationTemplateValue.className,
    });
  }

  onCancel() {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  resetForm() {
    this.substationTemplateValue = null;
    this.substationInstanceValue = null;
    this.substationOptions = [];
    this.selectedSubstationTemplateId = 0;
    this.skipNextInstanceFetch = false;
  }
}
